import {
  getParentUuid,
  getPrevSiblings,
} from '../../../containers/BulkSheetView/lib/tree'
import { IItemDelta } from '../../../../domain/value-object/ItemDeltaInputVO'
import {
  ProgramItemProjectPayload,
  ProgramManagementAddedItem,
  ProgramManagementDeltaBatchRequest,
  ProgramManagementDeletedItem,
  ProgramManagementEditedItem,
  ProgramManagementProgramDelta,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'
import { programItemRowService } from '../programItemRowService'
import { toCreateInput, toDeltaInput } from '../../Project/model'

// PROJECT行に同梱するプロジェクトの作成/更新ペイロードを組み立てる。
// 新規プロジェクト → create、既存プロジェクト編集 → update（元 detail との差分）。
const buildProjectPayload = (
  row: ProgramManagementRow
): ProgramItemProjectPayload | undefined => {
  if (!row.projectDraft) return undefined
  if (row.isNewProject) {
    return { create: toCreateInput(row.projectDraft) }
  }
  if (row.projectOriginal) {
    return { update: toDeltaInput(row.projectOriginal, row.projectDraft) }
  }
  return undefined
}

/**
 * 行の「自身の予定日」を返す。
 * 補完フィールド（scheduledStart/EndDateAggregated=true、表示用に配下集計が
 * 書き込まれた値）は自身の値ではないため、フィールド単位で未設定として扱う。
 */
const ownScheduledDate = (
  row: ProgramManagementRow
): ProgramManagementRow['scheduledDate'] => {
  const startDate = row.scheduledStartDateAggregated
    ? undefined
    : row.scheduledDate?.startDate
  const endDate = row.scheduledEndDateAggregated
    ? undefined
    : row.scheduledDate?.endDate
  if (!startDate && !endDate) return undefined
  return { startDate, endDate }
}

const buildDeltaValue = <T>(
  oldValue: T | undefined,
  newValue: T | undefined
): IItemDelta<T> | undefined => {
  if (oldValue === newValue) return undefined
  if (
    oldValue !== undefined &&
    newValue !== undefined &&
    JSON.stringify(oldValue) === JSON.stringify(newValue)
  ) {
    return undefined
  }
  return { oldValue, newValue }
}

const getPrevSiblingUuid = (
  rows: ProgramManagementRow[],
  row: ProgramManagementRow
): string | undefined => {
  const prevSiblings = getPrevSiblings(rows, row)
  return prevSiblings.length > 0
    ? prevSiblings[prevSiblings.length - 1].uuid
    : undefined
}

const buildAddedItem = (
  row: ProgramManagementRow,
  currentData: ProgramManagementRow[],
  programUuid: string
): ProgramManagementAddedItem => {
  const parentUuid = getParentUuid(row)
  const prevSiblingUuid = getPrevSiblingUuid(currentData, row)

  const item: ProgramManagementAddedItem = {
    uuid: row.uuid,
    programUuid,
    type: row.type,
    parentUuid,
    prevSiblingUuid,
  }

  if (programItemRowService.isGroupRow(row)) {
    item.programItemGroup = {
      uuid: row.groupUuid!,
      programUuid,
      code: row.code,
      displayName: row.displayName ?? '',
      scheduledDate: ownScheduledDate(row),
    }
  }

  if (programItemRowService.isProjectRow(row)) {
    item.projectUuid = row.projectUuid
    const project = buildProjectPayload(row)
    if (project) item.project = project
  }

  return item
}

const buildEditedItem = (
  row: ProgramManagementRow,
  original: ProgramManagementRow,
  currentData: ProgramManagementRow[],
  originalData: Map<string, ProgramManagementRow>
): ProgramManagementEditedItem | undefined => {
  const item: ProgramManagementEditedItem = {
    uuid: row.uuid,
    type: row.type,
  }

  const oldParentUuid = getParentUuid(original)
  const newParentUuid = getParentUuid(row)
  const parentDelta = buildDeltaValue(oldParentUuid, newParentUuid)
  if (parentDelta) {
    item.parentUuid = parentDelta
  }

  // prevSiblingUuid は変更有無にかかわらず必ず送る（サーバー側の並び順補正に必要）
  const oldPrevSiblingUuid = getPrevSiblingUuid(
    Array.from(originalData.values()),
    original
  )
  const newPrevSiblingUuid = getPrevSiblingUuid(currentData, row)
  item.prevSiblingUuid = {
    oldValue: oldPrevSiblingUuid,
    newValue: newPrevSiblingUuid,
  }

  if (programItemRowService.isGroupRow(row)) {
    const displayNameDelta = buildDeltaValue(
      original.displayName,
      row.displayName
    )
    const scheduledDateDelta = buildDeltaValue(
      ownScheduledDate(original),
      ownScheduledDate(row)
    )

    if (displayNameDelta || scheduledDateDelta) {
      item.programItemGroup = {
        uuid: row.groupUuid!,
      }
      if (displayNameDelta) {
        item.programItemGroup.displayName = displayNameDelta
      }
      if (scheduledDateDelta) {
        item.programItemGroup.scheduledDate = scheduledDateDelta
      }
    }
  }

  // 既存プロジェクト行の編集は更新（delta）のみ（別プロジェクトへの付け替えは不可）。
  if (
    programItemRowService.isProjectRow(row) &&
    row.projectDraft &&
    row.projectOriginal
  ) {
    item.project = toDeltaInput(row.projectOriginal, row.projectDraft)
  }

  return item
}

/**
 * プログラム行（自身の予定日）の編集内容からプログラム本体の更新 delta を構築する。
 * 編集が無い場合は undefined。
 */
const buildProgramDelta = (
  programUuid: string,
  row: ProgramManagementRow,
  originalData: Map<string, ProgramManagementRow>
): ProgramManagementProgramDelta | undefined => {
  if (!row.edited) return undefined
  const original = originalData.get(row.uuid)
  if (!original) return undefined

  const startDelta = buildDeltaValue(
    ownScheduledDate(original)?.startDate,
    ownScheduledDate(row)?.startDate
  )
  const endDelta = buildDeltaValue(
    ownScheduledDate(original)?.endDate,
    ownScheduledDate(row)?.endDate
  )
  if (!startDelta && !endDelta) return undefined

  const delta: ProgramManagementProgramDelta = { uuid: programUuid }
  if (startDelta) delta.scheduledStartDate = startDelta
  if (endDelta) delta.scheduledEndDate = endDelta
  return delta
}

export const buildDeltaBatchRequest = (
  programUuid: string,
  currentData: ProgramManagementRow[],
  originalData: Map<string, ProgramManagementRow>,
  deletedRows: ProgramManagementRow[]
): ProgramManagementDeltaBatchRequest => {
  const added: ProgramManagementAddedItem[] = []
  const edited: ProgramManagementEditedItem[] = []
  const deleted: ProgramManagementDeletedItem[] = []
  let program: ProgramManagementProgramDelta | undefined

  for (const row of currentData) {
    if (row.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM) {
      program = buildProgramDelta(programUuid, row, originalData) ?? program
      continue
    }

    if (row.added) {
      added.push(buildAddedItem(row, currentData, programUuid))
      continue
    }

    if (row.edited) {
      const original = originalData.get(row.uuid)
      if (!original) continue
      const editedItem = buildEditedItem(
        row,
        original,
        currentData,
        originalData
      )
      if (editedItem) {
        edited.push(editedItem)
      }
    }
  }

  for (const row of deletedRows) {
    if (!row.added) {
      deleted.push({ uuid: row.uuid })
    }
  }

  return { program, added, edited, deleted }
}
